import { useToast } from "@design";
import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  type ContextInfoData,
  chatGetSessionQueryKey,
  chatListSessionsQueryKey,
  errorMessage,
  listActiveOperationsQueryKey,
  listSessionDocumentsQueryKey,
} from "@/api";
import { refreshBudget } from "@/features/shell/usage/refreshBudget";
import type { StreamStatus } from "@/streaming/useOperationStream";
import type { AskUserPrompt } from "./AskUserCard";
import type { PendingTurn } from "./ChatThread";
import { type ChatHost, chatHost } from "./chatHost";
import type { ConnectorConnectRequest, ExtensionAccessRequest } from "./chatStreamReducer";
import type { SentAttachment } from "./MessageAttachments";
import { rememberStopped, stoppedHere } from "./stoppedReplies";
import { chatSessionRequest } from "./useChatSession";
import { useOnChatSwitch, useOpenCreatedChat } from "./useChatSwitchKey";
import { useReattachTurn } from "./useReattachTurn";
import { type SendInput, useSendMessage } from "./useSendMessage";

/** Refused sends the chat explains from their codes (backend text is English and secondary). */
const SEND_ERROR_CODES = [
  "budget_exceeded",
  "CONFIRMATION_NOT_PENDING",
  "CONFIRMATION_NOT_OWNER",
  "CONFIRMATION_INVALID",
  "RESEARCH_IN_PROGRESS",
  "run_in_progress",
  "ATTACHMENTS_NOT_IN_CHAT",
] as const;
type SendErrorCode = (typeof SEND_ERROR_CODES)[number];

function sendErrorCode(error: unknown): SendErrorCode | null {
  const code = error instanceof ApiError ? error.code : null;
  return SEND_ERROR_CODES.find((known) => known === code) ?? null;
}

/** The reply a `run_in_progress` refusal says holds the chat. */
function holderOf(error: unknown): string | undefined {
  const detail = error instanceof ApiError ? (error.body as { detail?: { operation_id?: unknown } })?.detail : null;
  return typeof detail?.operation_id === "string" ? detail.operation_id : undefined;
}

/** What a refused send says. */
function sendErrorText(t: TFunction<"chat">, error: unknown): string {
  const code = sendErrorCode(error);
  // A reply stopped here whose worker died still holds the chat until its lease runs out (MHS-863).
  if (code === "run_in_progress" && stoppedHere(holderOf(error))) {
    return t("stalled.stoppedStillHolding");
  }
  return code ? t(`reply.errorCodes.${code}`) : errorMessage(error, t("errors.sendFailed"));
}

/** Whether `next` is the turn `current` already shows: setting it again while rendering would render forever. */
function sameTurn(next: PendingTurn, current: PendingTurn | null): boolean {
  return (
    current !== null &&
    next.opId === current.opId &&
    next.prompt === current.prompt &&
    next.baseCount === current.baseCount
  );
}

/** The turn while its reply is still being written, else null. */
function liveTurn(turn: PendingTurn | null): PendingTurn | null {
  return turn && !turn.settled ? turn : null;
}

/** Stop pressed on the live turn `opId`: its reply stops the work, and ends here if that isn't confirmed in time. */
function requestStop(opId: string) {
  return (turn: PendingTurn | null) => (turn?.opId === opId && !turn.settled ? { ...turn, stopRequested: true } : turn);
}

type RetryAfterStop = { sessionUuid: string; prompt: string } | null;

/** The message to resend after Retry stopped its reply, once its chat can send. */
function promptToRetry(retry: RetryAfterStop, sessionUuid: string | undefined, busy: boolean): string | null {
  return retry && !busy && retry.sessionUuid === sessionUuid ? retry.prompt : null;
}

/**
 * Sending and the live turn. Saved messages are the truth: a completed reply
 * is replaced by its saved version; a failed or stopped one stays on screen.
 * A reply still running when a chat opens (after a reload) is picked up again.
 */
export function useChatTurns({
  sessionUuid,
  chatKey,
  savedCount,
  refetchSession,
  host = chatHost,
  trackContext = false,
}: Readonly<{
  sessionUuid: string | undefined;
  /** `useChatSwitchKey`: a question, permission or sign-in the last reply asked for stays in its chat. */
  chatKey: number;
  savedCount: number;
  refetchSession: () => Promise<unknown>;
  host?: ChatHost;
  /** Keep the reply's context-window reports for the composer's meter (the user's "Show context usage"). */
  trackContext?: boolean;
}>) {
  const { t } = useTranslation("chat");
  const toast = useToast();
  const openCreated = useOpenCreatedChat();
  const queryClient = useQueryClient();
  const send = useSendMessage();
  const [pending, setPending] = useState<PendingTurn | null>(null);
  // The files of the message being sent, shown on it until its saved copy is (MHS-1007).
  const [sending, setSending] = useState<SentAttachment[]>([]);
  const [askUser, setAskUser] = useState<AskUserPrompt | null>(null);
  // The permission the last reply needed, with the prompt to retry once it's allowed.
  const [extensionAccess, setExtensionAccess] = useState<(ExtensionAccessRequest & { prompt: string }) | null>(null);
  const [connectorConnect, setConnectorConnect] = useState<(ConnectorConnectRequest & { prompt: string }) | null>(null);
  // The last reply's context-window report, kept until the next send (as frontend/ did). Not persisted.
  const [context, setContext] = useState<{ sessionUuid: string; info: ContextInfoData } | null>(null);
  // Replies this page saw end; never reattached, even while a stale active-operations list still names them.
  const [ended, setEnded] = useState<ReadonlySet<string>>(() => new Set());
  // The message of a reply stopped by Retry, to send again now that it has ended.
  const [retry, setRetry] = useState<RetryAfterStop>(null);
  useOnChatSwitch(chatKey, () => {
    setAskUser(null);
    setExtensionAccess(null);
    setConnectorConnect(null);
  });

  const activePending = pending?.sessionUuid === (sessionUuid ?? null) ? pending : null;
  // A reply still running that this page isn't showing (after a reload, say) becomes the turn, unless a reply
  // sent from here is still live; one picked up before its message was saved moves under it once it is. Adjusted
  // while rendering (like useChatSwitchKey), so it shows from the first paint.
  const live = liveTurn(activePending);
  const liveHere = live !== null;
  const reattach = useReattachTurn(
    sessionUuid,
    activePending,
    (opId) => liveHere || opId === pending?.opId || ended.has(opId) || stoppedHere(opId),
  );
  if (reattach && !sameTurn(reattach, pending)) {
    if (reattach.opId !== pending?.opId) {
      setAskUser(null);
      setExtensionAccess(null);
      setContext(null);
    }
    setPending(reattach);
  }

  // Leaving a chat drops its reply if that's still going: back in the chat, a fresh list picks it up again if it
  // still runs. Kept instead, it could outlive its operation and fail to stream again (MHS-793). One stopped here
  // goes too: its run may still be listed as running (its worker died), and shown again it would stream again. The
  // chat is read again on the way back, since its reply may have been saved meanwhile.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const sessionRef = useRef(sessionUuid);
  sessionRef.current = sessionUuid;
  useEffect(() => {
    if (!sessionUuid) return;
    return () => {
      const left = pendingRef.current;
      if (left?.sessionUuid !== sessionUuid) return;
      if (left.settled && !stoppedHere(left.opId)) return;
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: chatGetSessionQueryKey({ path: { session_uuid: sessionUuid } }) });
    };
  }, [sessionUuid, queryClient]);

  /** `replaceFrom`: Try again (`input.retryOf`) hides the saved messages from this index, the reply it replaces. */
  const start = (
    input: Omit<SendInput, "sessionUuid">,
    onFailed: () => void,
    files: SentAttachment[] = [],
    replaceFrom?: number,
  ) => {
    if (send.isPending || (activePending && !activePending.settled)) return;
    setSending(files);
    setAskUser(null);
    setExtensionAccess(null);
    setConnectorConnect(null);
    setContext(null);
    send.mutate(
      { ...input, sessionUuid: sessionUuid ?? null, source: host.source },
      {
        onSuccess: (result) => {
          setPending({
            sessionUuid: result.sessionUuid,
            prompt: input.content,
            attachments: files.length > 0 ? files : undefined,
            continuation: input.continuation,
            decision: input.confirmation ? true : undefined,
            replacing: input.retryOf ? true : undefined,
            opId: result.opId,
            baseCount: replaceFrom ?? savedCount,
            startedAt: new Date().toISOString(),
          });
          // The decision is recorded before the reply starts, so its card can show it now rather than at the end.
          if (input.confirmation) void refetchSession();
          // Still the same chat, now with an id: nothing the person set up in it is cleared (MHS-1039).
          if (!sessionUuid) openCreated(host.pathFor(result.sessionUuid));
        },
        onError: (error) => {
          onFailed();
          // A send refused for budget (429 budget_exceeded) means the cached budget is out of date.
          void refreshBudget(queryClient);
          const code = sendErrorCode(error);
          // A proposal decided elsewhere or replaced meanwhile: its card shows what happened to it.
          if (code === "CONFIRMATION_NOT_PENDING") void refetchSession();
          // A reply this page didn't know of is being written in the chat (another tab, say): it shows here.
          if (code === "run_in_progress")
            void queryClient.invalidateQueries({ queryKey: listActiveOperationsQueryKey() });
          toast.error(sendErrorText(t, error));
        },
      },
    );
  };

  const settle = async (status: StreamStatus, retryIt: boolean) => {
    const opId = pending?.opId;
    // Retry stopped it to send its message again; one that finished meanwhile has its answer.
    const again =
      retryIt && status !== "completed" && pending?.prompt
        ? { sessionUuid: pending.sessionUuid, prompt: pending.prompt }
        : null;
    await queryClient.cancelQueries({
      queryKey: chatGetSessionQueryKey(chatSessionRequest(pending?.sessionUuid ?? "")),
    });
    await Promise.all([
      refetchSession(),
      queryClient.invalidateQueries({ queryKey: chatListSessionsQueryKey() }),
      // Every reply spends budget, and one refused for budget ends here too.
      refreshBudget(queryClient),
      // Replies can create or revise documents.
      queryClient.invalidateQueries({
        queryKey: listSessionDocumentsQueryKey({ path: { session_uuid: pending?.sessionUuid ?? "" } }),
      }),
      queryClient.invalidateQueries({ queryKey: listActiveOperationsQueryKey() }),
    ]);
    if (opId) setEnded((done) => new Set(done).add(opId));
    if (opId && status === "cancelled") rememberStopped(opId);
    setPending((p) => (p && status !== "completed" ? { ...p, settled: true } : null));
    // Only while I'm still in that chat: coming back to it later mustn't send the message out of the blue.
    if (again && again.sessionUuid === sessionRef.current) setRetry(again);
  };

  const pendingSession = pending?.sessionUuid;
  const onContext = useCallback(
    (info: ContextInfoData) => pendingSession && setContext({ sessionUuid: pendingSession, info }),
    [pendingSession],
  );

  const busy = send.isPending || liveHere;
  return {
    start,
    /** A reply is being written (not just sent): it can be stopped. */
    generating: liveHere,
    stop: () => live && setPending(requestStop(live.opId)),
    settle,
    /** The message to send again for Retry, once its reply has ended. */
    retryPrompt: promptToRetry(retry, sessionUuid, busy),
    retried: () => setRetry(null),
    pending: activePending,
    // Try again answers a saved question: it isn't shown again.
    optimisticPrompt: send.isPending && !send.variables?.retryOf ? send.variables?.content : undefined,
    optimisticAttachments: send.isPending ? sending : undefined,
    optimisticContinuation: send.isPending && send.variables?.continuation === true,
    busy,
    askUser: busy ? null : askUser,
    setAskUser,
    extensionAccess: busy ? null : extensionAccess,
    onExtensionAccess: (request: ExtensionAccessRequest) =>
      activePending && setExtensionAccess({ ...request, prompt: activePending.prompt ?? "" }),
    allowedExtensionAccess: () => {
      setExtensionAccess(null);
      return connectorConnect ? null : (extensionAccess?.prompt ?? null);
    },
    dismissExtensionAccess: () => {
      setExtensionAccess(null);
      setConnectorConnect(null);
    },
    connectorConnect: busy || extensionAccess ? null : connectorConnect,
    onConnectorConnect: (request: ConnectorConnectRequest) =>
      activePending && setConnectorConnect({ ...request, prompt: activePending.prompt ?? "" }),
    dismissConnectorConnect: () => setConnectorConnect(null),
    context: trackContext && context && context.sessionUuid === sessionUuid ? context.info : null,
    onContext: trackContext ? onContext : undefined,
  };
}
